import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import moment from "moment/min/moment-with-locales";
import { useCallback, useMemo, useState } from "react";
import { Chart } from "react-chartjs-2";
import { formatDemographicText } from "./HelperFunctions";
import ClocksModal from "./ClocksModal";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function TslChart({ data, grouping, resolvedRadios, resolvedDemos, moduleSelection, isSessions = false }) {
    const [showModal, setShowModal] = useState(false);
    const [selectedData, setSelectedData] = useState(null);

    const handleChartClick = useCallback(
        (event, elements) => {
            if (elements.length > 0 && (grouping === 0 || grouping === 1)) {
                const elementIndex = elements[0].index;
                const datasetIndex = elements[0].datasetIndex;
                const selectedDataset = data[datasetIndex];
                const selectedRating = selectedDataset.tsl[elementIndex];

                setSelectedData({
                    radioId: selectedDataset.radio.radio_id,
                    date: moment(selectedRating.basis_hour).toDate(),
                    hour: moment(selectedRating.basis_hour).hour(),
                    startAudioTime: moment(selectedRating.basis_hour).diff(moment(selectedRating.basis_hour).startOf("hour"), "seconds"),
                });

                setShowModal(true);
            }
        },
        [data, grouping],
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            normalized: true,
            datasets: {
                line: {
                    pointRadius: data[0].tsl.length > 1000 ? 0 : grouping < 3 ? 1 : 3,
                    borderWidth: 3,
                    borderJoinStyle: "round",
                    pointHoverRadius: 4,
                    pointHitRadius: 4,
                },
            },
            plugins: {
                legend: { labels: { usePointStyle: true } },
                datalabels: { display: false },
                tooltip: {
                    callbacks: {
                        label: function (item) {
                            const value = item.raw;
                            const formatted = isSessions ? value : moment.utc(moment.duration(value, "s").asMilliseconds()).format("HH:mm:ss");
                            return `${item.dataset.label}: ${formatted}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: {
                        minRotation: 90,
                        maxTicksLimit: 31,
                        maxRotation: 90,
                        sampleSize: 5,
                    },
                },
                y: {
                    type: "linear",
                    ticks: {
                        callback: function (value, index, values) {
                            return isSessions ? value : moment.utc(moment.duration(value, "s").asMilliseconds()).format("HH:mm:ss");
                        },
                    },
                },
            },
            interaction: { mode: "index" },
            onClick: grouping < 3 ? handleChartClick : undefined,
        }),
        [data, grouping, handleChartClick, isSessions],
    );

    const chartData = useMemo(() => {
        if (!data) return;
        const colors = ["#FEBE10", "#519872", "#D5573B", "#396A93"];
        const labels = data[0].tsl.map(x => moment(x.basis_hour).format("ddd L LT"));

        const getLabel = (radio, demo) => {
            const radioName = resolvedRadios.find(rr => rr.radio_id === radio.radio_id)?.radio_name;
            const demoText = formatDemographicText(resolvedDemos.find(rd => rd.demo_id === radio.demo_id));
            return `${radioName} - ${demoText}`;
        };

        const datasets = data.map((x, i) => {
            return {
                type: "line",
                backgroundColor: colors[i],
                pointBackgroundColor: colors[i],
                borderColor: colors[i],
                label: getLabel(x.radio, x.radio),
                data: x.tsl.map(r => isSessions ? r.count : r.tsl),
            };
        });
        datasets.push({
            type: "line",
            backgroundColor: "#142533",
            pointBackgroundColor: "#142533",
            borderColor: "#142533",
            label: `${getLabel(data[0].radio, data[0].radio)} (Average)`,
            data: data[0].tsl.map(x => isSessions ? x.count_average : x.tsl_average),
        });
        return { labels, datasets };
    }, [data, resolvedDemos, resolvedRadios, isSessions]);

    return (
        <div className=" overflow-auto flex-grow-1">
            <div className=" h-100  ratings-chart">
                <Chart data={chartData} options={chartOptions} />
            </div>
            {showModal && selectedData && (
                <ClocksModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    radioId={selectedData.radioId}
                    date={selectedData.date}
                    hour={selectedData.hour}
                    startAudioTime={selectedData.startAudioTime}
                    addToCollectionAllowed={true}
                />
            )}
        </div>
    );
}
